import { useMemo, useState } from 'react';
import { useAddTeamMember, useRemoveTeamMember, useTeamCandidates, useTeamMembers } from '../api/hooks';
import type { Team, TeamMember } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { EmptyState, ErrorBanner, Modal } from './ui';

/**
 * A team's roster: who is under this manager, and — for an administrator — the
 * controls to change it.
 *
 * Shared by the manager's own page and the admin teams screen rather than
 * written twice, because the rules it has to communicate are identical in both
 * places. Who is on a team is set org-wide, so a manager reads this and an
 * administrator edits it; the API enforces the same split, and hiding the
 * buttons here only saves the manager a request that would 403.
 */
export function TeamRoster({ team }: { team: Team }) {
  const { user } = useAuth();
  const canEdit = user?.scope === 'all';
  const { data: members = [], isPending, error } = useTeamMembers(team.id);
  const remove = useRemoveTeamMember();
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<TeamMember | null>(null);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 650, fontSize: 14.5 }}>Team Specialists on this team</div>
          <p className="hint" style={{ marginTop: 4, marginBottom: 0, maxWidth: '56ch' }}>
            {team.manager
              ? `Leads route to ${team.manager.full_name} first, then out to these people.`
              : 'This team has no manager yet, so nothing routes to it.'}
          </p>
        </div>
        {canEdit && (
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            + Add team specialist
          </button>
        )}
      </div>

      <ErrorBanner error={error ?? remove.error} />

      {isPending ? (
        <div className="skeleton" style={{ height: 80, borderRadius: 10, marginTop: 12 }} />
      ) : members.length === 0 ? (
        <EmptyState
          title="Nobody on this team yet"
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setAdding(true)}>
                Add the first team specialist
              </button>
            ) : undefined
          }
        >
          Until someone is on the roster, auto-distribute has nowhere to send leads and they stay
          with the manager.
          {!canEdit && ' Ask an administrator to add team specialists to your team.'}
        </EmptyState>
      ) : (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="num">Leads</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} style={m.is_active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <strong>{m.full_name}</strong>
                    {!m.is_active && <span className="tag" style={{ marginLeft: 6 }}>deactivated</span>}
                  </td>
                  <td className="mono">{m.email}</td>
                  <td>{m.role_name ?? <span style={{ color: 'var(--faint)' }}>No role</span>}</td>
                  <td className="num">{m.lead_count}</td>
                  {canEdit && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => setConfirmRemove(m)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && canEdit && <AddMemberModal team={team} onClose={() => setAdding(false)} />}

      {confirmRemove && (
        <Modal
          title={`Remove ${confirmRemove.full_name} from ${team.name}?`}
          onClose={() => setConfirmRemove(null)}
          width={520}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={async () => {
                  try {
                    await remove.mutateAsync({ teamId: team.id, userId: confirmRemove.id });
                    setConfirmRemove(null);
                  } catch {
                    setConfirmRemove(null);
                  }
                }}
              >
                {remove.isPending ? <span className="spinner" /> : 'Remove'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            They stop receiving new leads from this team, and drop out of the auto-distribute
            rotation immediately.
          </p>
          <ul style={{ marginBottom: 0 }}>
            {confirmRemove.lead_count > 0 && (
              <li>
                <strong>
                  {confirmRemove.lead_count} lead{confirmRemove.lead_count === 1 ? '' : 's'} stay
                  assigned to them
                </strong>{' '}
                — reassign those first if someone else should pick them up.
              </li>
            )}
            <li>Their account and history are untouched; only the team link is removed.</li>
            <li>They can be added to this or another team at any time.</li>
          </ul>
        </Modal>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function AddMemberModal({ team, onClose }: { team: Team; onClose: () => void }) {
  const { data: candidates = [], isPending } = useTeamCandidates(team.id);
  const { data: members = [] } = useTeamMembers(team.id);
  const add = useAddTeamMember();
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<unknown>(null);

  // The candidate list includes this team's own members (so the picker can be
  // reused for display); here they are already on the roster, so drop them.
  const onRoster = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const available = candidates.filter((c) => !onRoster.has(c.id) && c.id !== team.manager?.id);

  // Administrators also see people who are currently under another manager.
  // Splitting the two groups keeps a move from happening by accident: picking
  // out of the second list takes someone off another team.
  const free = available.filter((c) => c.teams.length === 0);
  const elsewhere = available.filter((c) => c.teams.length > 0);
  const picked = available.find((c) => c.id === userId);
  const movingFrom = picked?.teams[0]?.name ?? null;

  async function submit() {
    setError(null);
    try {
      await add.mutateAsync({ teamId: team.id, userId });
      onClose();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title={`Add to ${team.name}`}
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!userId || add.isPending} onClick={submit}>
            {add.isPending ? <span className="spinner" /> : 'Add to team'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      {isPending ? (
        <div className="skeleton" style={{ height: 60, borderRadius: 10 }} />
      ) : available.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--muted)' }}>
          Nobody is available to add. Someone can only be under one manager at a time — ask an
          administrator to create the account, or to move someone off their current team.
        </p>
      ) : (
        <div className="field">
          <label htmlFor="add-member">Person</label>
          <select id="add-member" value={userId} autoFocus onChange={(e) => setUserId(e.target.value)}>
            <option value="">Select someone…</option>
            {free.length > 0 && (
              <optgroup label="Not on a team">
                {free.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                    {u.role ? ` — ${u.role.name}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {elsewhere.length > 0 && (
              <optgroup label="On another team — will be moved">
                {elsewhere.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} — currently on {u.teams[0]?.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {movingFrom ? (
            <div className="hint" style={{ color: 'var(--warn)' }}>
              This takes {picked?.full_name} off {movingFrom}. A team specialist belongs to one manager at
              a time, so they stop receiving that team's leads — the ones already assigned to them
              stay where they are.
            </div>
          ) : (
            <div className="hint">
              A team specialist belongs to one manager at a time, so nobody appears in two rotations.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
